import React, { useCallback, useMemo, useState } from 'react'
import { EduJoyride } from '@edulastic/common'
import { tutorials } from '@edulastic/constants'
import { TRACK_IDS } from '@edulastic/constants/educationJoyrides'
import { segmentApi } from '@edulastic/api'
import { ACTIONS, EVENTS, STATUS } from 'react-joyride'
import {
  ACTION_HUB_JOYRIDE_CTA_MAP,
  ACTION_HUB_JOYRIDE_SPOTLIGHT_PADDING,
  ACTION_HUB_JOYRIDE_TARGETS,
} from './constants'
import ActionHubJoyrideTooltip from './ActionHubJoyrideTooltip'

const getJoyrideSteps = (isPearyVisibleInActionHub) => {
  const steps = [
    {
      target: ACTION_HUB_JOYRIDE_TARGETS.FAB,
      content: 'Welcome to the new Help and Feedback Action Hub.',
      disableBeacon: true,
      placement: 'top',
      spotlightPadding: ACTION_HUB_JOYRIDE_SPOTLIGHT_PADDING.FAB,
    },
  ]

  if (isPearyVisibleInActionHub) {
    steps.push({
      target: ACTION_HUB_JOYRIDE_TARGETS.PEARY,
      content:
        'Use Peary AI to get help, discover features, and find answers faster.',
      placement: 'left',
      spotlightPadding: ACTION_HUB_JOYRIDE_SPOTLIGHT_PADDING.MENU_ITEM,
    })
  }

  steps.push(
    {
      target: ACTION_HUB_JOYRIDE_TARGETS.FEEDBACK,
      content:
        'Share feedback or feature requests directly without leaving your workflow.',
      placement: 'left',
      spotlightPadding: ACTION_HUB_JOYRIDE_SPOTLIGHT_PADDING.MENU_ITEM,
    },
    {
      target: ACTION_HUB_JOYRIDE_TARGETS.ARTICLES,
      content: 'Open help center anytime to learn more and find answers.',
      placement: 'left',
      spotlightPadding: ACTION_HUB_JOYRIDE_SPOTLIGHT_PADDING.MENU_ITEM,
    },
    {
      target: ACTION_HUB_JOYRIDE_TARGETS.SUPPORT,
      content: 'Contact our Support team whenever you need assistance.',
      placement: 'left',
      spotlightPadding: ACTION_HUB_JOYRIDE_SPOTLIGHT_PADDING.MENU_ITEM,
    }
  )
  return steps
}

const ActionHubJoyride = ({
  isPearyVisibleInActionHub,
  viewedTutorials,
  setViewedTutorials,
  onExpandMenu,
  onCollapseMenu,
}) => {
  const [run, setRun] = useState(true)
  const [stepIndex, setStepIndex] = useState(0)

  const steps = useMemo(() => getJoyrideSteps(isPearyVisibleInActionHub), [
    isPearyVisibleInActionHub,
  ])

  const markTutorialComplete = useCallback(() => {
    if (!viewedTutorials.includes(tutorials.actionHubJoyride)) {
      setViewedTutorials([...viewedTutorials, tutorials.actionHubJoyride])
    }
    setRun(false)
    onCollapseMenu?.()
  }, [onCollapseMenu, setViewedTutorials, viewedTutorials])

  const trackStep = useCallback((CTA_Name, userResponse) => {
    if (!CTA_Name) return
    segmentApi.genericEventTrack('Joyride', {
      CTA_Name,
      userResponse,
      pagePath: window.location.pathname,
      joyride_track: TRACK_IDS.UNIFIED_ACTION_HUB,
    })
  }, [])

  const joyrideCallback = useCallback(
    (data) => {
      const { action, index, status, type, step } = data
      const ctaName = step?.target
        ? ACTION_HUB_JOYRIDE_CTA_MAP[step.target]
        : null

      if (action === ACTIONS.CLOSE) {
        trackStep(ctaName, 'Close')
        markTutorialComplete()
        return
      }

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        markTutorialComplete()
        return
      }

      if (type === EVENTS.TOUR_START) {
        trackStep(ACTION_HUB_JOYRIDE_CTA_MAP[steps[0]?.target], 'clickCTA')
        return
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)

        if (
          (action === ACTIONS.NEXT && nextIndex < steps.length) ||
          (action === ACTIONS.PREV && nextIndex >= 0)
        ) {
          trackStep(
            ACTION_HUB_JOYRIDE_CTA_MAP[steps[nextIndex]?.target],
            'clickCTA'
          )
        }

        if (nextIndex === 0) {
          onCollapseMenu?.()
        } else if (nextIndex >= 1) {
          onExpandMenu?.()
        }

        if (nextIndex >= steps.length) {
          markTutorialComplete()
          return
        }

        setStepIndex(nextIndex)
      }
    },
    [markTutorialComplete, onCollapseMenu, onExpandMenu, steps, trackStep]
  )

  if (!run) return null

  return (
    <EduJoyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress={false}
      showSkipButton={false}
      disableOverlayClose
      disableScrolling
      callback={joyrideCallback}
      locale={{
        back: 'Previous',
        next: 'Next',
        last: 'Got it',
        close: 'Got it',
      }}
      options={{ zIndex: 1015, width: 282 }}
      tooltipComponent={ActionHubJoyrideTooltip}
      styles={{
        tooltip: {
          padding: '12px 16px',
          textAlign: 'left',
        },
        tooltipContent: {
          fontSize: 15,
          fontWeight: 400,
          lineHeight: '22px',
          color: '#000',
          margin: 0,
          padding: 0,
          textAlign: 'left',
        },
        tooltipFooter: {
          marginTop: 12,
        },
        buttonBack: {
          fontSize: 14,
          fontWeight: 600,
        },
        buttonNext: {
          fontSize: 14,
          fontWeight: 600,
        },
        buttonClose: {
          position: 'static',
        },
      }}
    />
  )
}

export default ActionHubJoyride
